import { useState } from "react";
import { depascalize, ListTableSort, SortOrder } from "sprint-common-web";

import { FileRecordAssociation } from "../../../../../common/types/FileRecordAssociation";
import { FileRecordStatus } from "../../../../../common/types/FileRecordStatus";
import { RecordType } from "../../../../../common/types/RecordType";
import { applicableFileRecordLookupStatuses } from "../utils/applicableFileRecordLookupStatuses";

export interface FileRecordLookupListingFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    content?: string;
    deliveryFileName?: string;
    filePath?: string;
    deliveryRef?: string;
    statuses?: number[];
    associations?: number[];
    recordTypes?: number[];
    createdDateFrom?: Date;
    createdDateTo?: Date;
}

const defaultStatuses = applicableFileRecordLookupStatuses.filter((status) => status !== FileRecordStatus.QcDone);

const defaultRecordTypes = [RecordType.Video, RecordType.Audio, RecordType.Subtitle];
const defaultAssociations = [FileRecordAssociation.Associated, FileRecordAssociation.NonAssociated];

const defaultFilters: FileRecordLookupListingFilters = {
    page: 1,
    pageSize: 10,
    sortBy: "updatedDate",
    sortOrder: "desc",
    filePath: "",
    deliveryFileName: "",
    deliveryRef: "",
    content: "",
    statuses: defaultStatuses,
    recordTypes: defaultRecordTypes,
    associations: defaultAssociations,
    createdDateFrom: undefined,
    createdDateTo: undefined
};

export const useFileRecordLookupListingFilters = () => {
    const [filters, setFilters] = useState<FileRecordLookupListingFilters>(defaultFilters);

    const setSort = (sort: ListTableSort) => {
        setFilters((prevFilters: FileRecordLookupListingFilters) => ({
            ...prevFilters,
            sortBy: sort.by,
            sortOrder: sort.order,
            page: 1
        }));
    };

    const setPage = (page: number) => {
        setFilters((prevFilters: FileRecordLookupListingFilters) => ({ ...prevFilters, page }));
    };

    const setPageSize = (pageSize: number): void => {
        setFilters((prevFilters: FileRecordLookupListingFilters) => ({ ...prevFilters, pageSize, page: 1 }));
    };

    const setFilePath = (filePath?: string): void => {
        setFilters((prevFilters: FileRecordLookupListingFilters) => ({ ...prevFilters, filePath, page: 1 }));
    };

    const setDeliveryRef = (deliveryRef?: string): void => {
        setFilters((prevFilters: FileRecordLookupListingFilters) => ({ ...prevFilters, deliveryRef, page: 1 }));
    };

    const setDeliveryFileName = (deliveryFileName?: string): void => {
        setFilters((prevFilters: FileRecordLookupListingFilters) => ({ ...prevFilters, deliveryFileName, page: 1 }));
    };

    const setContent = (content?: string): void => {
        setFilters((prevFilters: FileRecordLookupListingFilters) => ({ ...prevFilters, content, page: 1 }));
    };

    const setStatuses = (statuses?: number[]): void => {
        setFilters((prevFilters: FileRecordLookupListingFilters) => ({ ...prevFilters, statuses, page: 1 }));
    };

    const setNotifications = (notifications?: number[]): void => {
        setFilters((prevFilters: FileRecordLookupListingFilters) => ({
            ...prevFilters,
            notifications,
            page: 1
        }));
    };

    const setRecordTypes = (recordTypes?: number[]): void => {
        setFilters((prevFilters: FileRecordLookupListingFilters) => ({
            ...prevFilters,
            recordTypes,
            page: 1
        }));
    };

    const setAssociations = (associations?: number[]): void => {
        setFilters((prevFilters: FileRecordLookupListingFilters) => ({
            ...prevFilters,
            associations,
            page: 1
        }));
    };

    const setCreatedDateFrom = (date?: Date): void => {
        setFilters((prevFilters: FileRecordLookupListingFilters) => ({
            ...prevFilters,
            createdDateFrom: date,
            page: 1
        }));
    };

    const setCreatedDateTo = (date?: Date): void => {
        setFilters((prevFilters: FileRecordLookupListingFilters) => ({ ...prevFilters, createdDateTo: date, page: 1 }));
    };

    const clearFilters = (): void => {
        setFilters(defaultFilters);
    };

    const statusOptions = applicableFileRecordLookupStatuses.map((status) => ({
        key: status,
        text: depascalize(FileRecordStatus[status])
    }));

    const recordTypeOptions = defaultRecordTypes.map((type) => ({
        key: type,
        text: depascalize(RecordType[type])
    }));

    const associationsOptions = defaultAssociations.map((item) => ({
        key: item,
        text: depascalize(FileRecordAssociation[item])
    }));

    return {
        filters,
        setSort,
        setPage,
        setPageSize,
        setFilePath,
        setDeliveryRef,
        setDeliveryFileName,
        setStatuses,
        setContent,
        setNotifications,
        setRecordTypes,
        setAssociations,
        setCreatedDateFrom,
        setCreatedDateTo,
        clearFilters,
        statusOptions,
        recordTypeOptions,
        associationsOptions
    };
};
